import React, { useRef, useEffect } from 'react';
import _ from 'lodash';
import { Box, Typography, ListItem, ListItemButton, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';
import ValueList from './ValueList';

import logging from 'utils/logging';
const log = logging.getLogger("CategoryItem");

// Define brand colors
const ACCENT_TEAL = '#2ec4b6';
const ACCENT_YELLOW = '#ffe066';
const TEXT_COLOR = '#222';

const Root = styled(Box)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1.5),
  },
}));

const CategoryContainer = styled(ListItem)(({ theme }) => ({
  width: '100%',
  padding: 0,
  marginBottom: theme.spacing(1),
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isSelected'
})(({ theme, isSelected }) => ({
  backgroundColor: isSelected ? `${ACCENT_TEAL}15` : '#fff',
  borderRadius: theme.spacing(1),
  border: `2px solid ${isSelected ? ACCENT_TEAL : ACCENT_TEAL}`,
  padding: theme.spacing(2),
  minHeight: '56px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    backgroundColor: `${ACCENT_TEAL}15`,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    minHeight: '48px',
  },
}));

const CategoryName = styled(Typography)(({ theme }) => ({
  fontFamily: "'Helvetica Inserat', Arial, sans-serif",
  fontSize: '1.2rem',
  fontWeight: 600,
  color: TEXT_COLOR,
  textAlign: 'center',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
  },
}));

const StatusBoxContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: theme.spacing(0.5),
}));

const StatusBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isAvailable'
})(({ theme, isAvailable }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '2px',
  backgroundColor: isAvailable ? ACCENT_YELLOW : '#cccccc',
  boxShadow: isAvailable ? `0 0 4px ${ACCENT_YELLOW}80` : 'none',
  transition: 'all 0.2s ease-in-out',
}));

const ValueListContainer = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}));

const StyledCollapse = styled(Collapse)(({ theme }) => ({
  transition: theme.transitions.create('all', {
    duration: theme.transitions.duration.standard,
    easing: theme.transitions.easing.easeInOut,
  }),
}));

const CategoryItem = ({ category, isSelected, onCategorySelect, onQuestionSelect }) => {
  const rootRef = useRef(null);
  const label = category.label;
  const questions = category.questions;

  // Auto-scroll the selected category to the top of the viewport
  useEffect(() => {
    if (isSelected && rootRef.current) {
      setTimeout(() => {
        rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }, [isSelected]);

  if (!questions || _.isEmpty(questions)) {
    return null;
  }
  
  // Get the status of each value in the category
  const statusBoxes = Array(5).fill(null).map((_, index) => {
    const question = questions[index];
    const isAvailable = question && !question.picked;
    
    return (
      <StatusBox 
        key={`status-${index}`}
        isAvailable={isAvailable}
        aria-label={isAvailable ? 'Available clue' : 'Completed clue'}
      />
    );
  });

  const handleClick = () => {
    if (!isSelected) {
      onCategorySelect(category);
    } else {
      onCategorySelect(null);
    }
  };

  return (
    <Root ref={rootRef}>
      <CategoryContainer>
        <StyledListItemButton 
          onClick={handleClick}
          isSelected={isSelected}
        >
          <CategoryName>
            {label.toUpperCase()}
          </CategoryName>
          <StatusBoxContainer>
            {statusBoxes}
          </StatusBoxContainer>
        </StyledListItemButton>
      </CategoryContainer>
      <StyledCollapse in={isSelected} timeout={300}>
        <ValueListContainer>
          <ValueList 
            category={category}
            onValueSelect={(question) => {
            //   onCategorySelect(null);
              // Handle question selection
              log.debug('Selected question:', question);
              onQuestionSelect(question);
            }}
          />
        </ValueListContainer>
      </StyledCollapse>
    </Root>
  );
};

export default CategoryItem; 